import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import humanTime from 'flarum/common/helpers/humanTime';
import SpeciesAssignModal from './SpeciesAssignModal';
import { promptLogin, loginRequiredPlaceholder, permissionDeniedPlaceholder } from '../utils/requireAccess';
const PAGE_LIMIT = 30;
/**
 * /missing-species - moderation-only (champis-species.viewMissingSpecies,
 * see extend.php) list of discussions in an identification-relevant forum
 * that have no species decision at all yet (see MissingSpeciesService on
 * the PHP side). Each row links to the discussion itself, and also has its
 * own "Espèce" button opening the same SpeciesAssignModal as
 * addSpeciesControl.js - same extension, so this is a plain import rather
 * than the flarum.reg cross-extension lookup SpeciesKeysBox.js needs for
 * champis-keys' viewer.
 *
 * /api/species-missing's own rows (see MissingSpeciesResource) are plain
 * flattened data (title/tagNames as strings), not real models - fine for
 * display, but SpeciesAssignModal needs a genuine Discussion instance (it
 * reads discussion.tags()/species()/speciesAssignmentMode() directly), so
 * openAssignModal() fetches that lazily on click rather than upfront for
 * every row (most rows in a given page view are never clicked).
 *
 * Real server-side pagination (unlike DescriptionsPage.js/
 * DeterminationKeysPage.js, which load their whole bounded dataset upfront
 * and reveal more of it client-side) - this list is a live moderation
 * queue that only grows over time, so "Charger davantage" here fetches
 * the next page from the server instead.
 */
export default class MissingSpeciesPage extends Page {
  discussions = [];
  offset = 0;
  moreAvailable = true;
  loading = false;
  error = false;
  assigningId = null;
  requiresLogin = false;
  permissionDenied = false;
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(app.translator.trans('champis-species.forum.missing_species_page.title'));
    this.discussions = [];
    this.offset = 0;
    this.moreAvailable = true;
    this.loading = false;
    this.error = false;
    this.assigningId = null;
    this.requiresLogin = false;
    this.permissionDenied = false;

    // MissingSpeciesResource's own Index endpoint rejects anyone without
    // champis-species.viewMissingSpecies (moderation-only) - checked here
    // rather than just letting the request below fail, so a guest gets a
    // login prompt (they might be a curator not yet signed in) and a
    // logged-in non-curator gets a clear "no permission" message instead
    // of both landing on the same generic error (see requireAccess.js's
    // own docblock).
    if (!app.session.user) {
      this.requiresLogin = true;
      promptLogin();
      return;
    }
    if (!app.forum.attribute('canViewMissingSpecies')) {
      this.permissionDenied = true;
      return;
    }
    this.load();
  }
  load() {
    this.loading = true;
    this.error = false;
    m.redraw();
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/species-missing',
      params: {
        page: {
          limit: PAGE_LIMIT,
          offset: this.offset
        }
      }
    }).then(response => {
      const items = (response.data || []).map(item => ({
        id: item.id,
        ...item.attributes
      }));
      this.discussions = this.discussions.concat(items);
      this.offset += items.length;
      this.moreAvailable = items.length === PAGE_LIMIT;
      this.loading = false;
      m.redraw();
    }).catch(() => {
      this.error = true;
      this.loading = false;
      m.redraw();
    });
  }
  view() {
    if (this.requiresLogin || this.permissionDenied) {
      return m("div", {
        className: "Page MissingSpeciesPage"
      }, m("div", {
        className: "container"
      }, this.requiresLogin ? loginRequiredPlaceholder(app.translator.trans('champis-species.forum.missing_species_page.login_required')) : permissionDeniedPlaceholder(app.translator.trans('champis-species.forum.missing_species_page.permission_denied'))));
    }
    return m("div", {
      className: "Page MissingSpeciesPage"
    }, m("div", {
      className: "container"
    }, m("h1", {
      className: "ChampisDiscussionList-header"
    }, m("i", {
      className: "icon fas fa-dna",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-species.forum.missing_species_page.title')), m("div", {
      className: "ChampisDescriptionsList"
    }, this.error ? m(Placeholder, {
      text: app.translator.trans('champis-species.forum.missing_species_page.error')
    }) : this.discussions.length === 0 && this.loading ? m(LoadingIndicator, null) : this.discussions.length === 0 ? m(Placeholder, {
      text: app.translator.trans('champis-species.forum.missing_species_page.empty')
    }) : m("ul", {
      className: "ChampisDescriptionsList-items"
    }, this.discussions.map(d => this.row(d)))), !this.error && this.moreAvailable && this.discussions.length > 0 && m("div", {
      className: "ChampisDescriptionsList-loadMore"
    }, m(Button, {
      className: "Button",
      loading: this.loading,
      onclick: () => this.load()
    }, app.translator.trans('champis-species.forum.missing_species_page.load_more_button')))));
  }
  row(discussion) {
    return m("li", {
      key: discussion.id
    }, m(Link, {
      href: app.route('discussion', {
        id: discussion.id
      }),
      className: "ChampisDescriptionsList-item"
    }, m("span", {
      className: "ChampisDescriptionsList-name"
    }, discussion.title), m("div", {
      className: "ChampisDescriptionsList-author"
    }, discussion.tagNames.join(' · '), ' — ', app.translator.trans('champis-species.forum.gallery.by', {
      name: discussion.authorName
    }), ' — ', humanTime(new Date(discussion.createdAt))), m(Button, {
      icon: "fas fa-dna",
      className: "Button MissingSpeciesPage-assignButton",
      loading: this.assigningId === discussion.id,
      onclick: e => {
        e.preventDefault();
        e.stopPropagation();
        this.openAssignModal(discussion);
      }
    }, app.translator.trans('champis-species.forum.missing_species_page.assign_button'))));
  }
  openAssignModal(discussion) {
    if (this.assigningId) return;
    this.assigningId = discussion.id;
    m.redraw();
    app.store.find('discussions', discussion.id).then(fullDiscussion => {
      this.assigningId = null;
      m.redraw();
      app.modal.show(SpeciesAssignModal, {
        discussion: fullDiscussion,
        onsubmit: entries => fullDiscussion.save({
          species: entries
        }).then(() => {
          // Assigned - this row no longer belongs on a "missing
          // species" list, so drop it rather than waiting for a full
          // reload to catch up.
          this.discussions = this.discussions.filter(d => d.id !== discussion.id);
          m.redraw();
        })
      });
    }).catch(() => {
      this.assigningId = null;
      m.redraw();
    });
  }
}
flarum.reg.add('champis-species', 'forum/components/MissingSpeciesPage', MissingSpeciesPage);